import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Globe2, BookOpenCheck, Image, Users, RefreshCw, Gauge, ShieldCheck, Flag } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoverageDashboard() {
  const { tr } = useI18n();
  const [data, setData] = useState(null);
  const [quality, setQuality] = useState(null);
  const [corrections, setCorrections] = useState(null);
  const [regionFilter, setRegionFilter] = useState("All");

  const load = () => {
    axios.get(`${API}/countries/coverage`).then((r) => setData(r.data)).catch(() => {});
    axios.get(`${API}/countries/quality`).then((r) => setQuality(r.data)).catch(() => {});
    axios.get(`${API}/corrections?status=pending`).then((r) => setCorrections(r.data)).catch(() => {});
  };

  const reviewCorrection = async (id) => {
    await axios.post(`${API}/corrections/${id}/review`);
    toast.success("Marked as reviewed");
    load();
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const startPregen = async () => {
    const r = await axios.post(`${API}/countries/pregen?limit=50`);
    toast.success(r.data.status === "started" ? "Pre-generation of top 50 countries started" : "Already running");
    load();
  };

  if (!data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center gap-3 text-[#2563EB]">
        <Loader2 className="animate-spin" /> <span className="font-bold">{tr("Loading coverage…")}</span>
      </div>
    );
  }

  const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
  const stats = [
    { icon: Globe2, label: "Countries in database", value: data.total, color: "#2563EB", bg: "#EAF2FD" },
    { icon: BookOpenCheck, label: "Full portals generated", value: `${data.enriched} (${pct(data.enriched, data.total)}%)`, color: "#16A34A", bg: "#F0FDF4" },
    { icon: Image, label: "With hero photo", value: Object.values(data.regions).reduce((a, r) => a + r.with_photo, 0), color: "#EA580C", bg: "#FFF7ED" },
    { icon: Users, label: "With population data", value: Object.values(data.regions).reduce((a, r) => a + r.with_population, 0), color: "#7C3AED", bg: "#F5F3FF" },
  ];

  return (
    <div data-testid="coverage-dashboard" className="pb-24 max-w-6xl mx-auto px-4 lg:px-8">
      <div className="py-12">
        <span className="wp-eyebrow">{tr("Admin · Country Completion")}</span>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-3">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-[#0F2557] tracking-tight">{tr("Global Coverage")}</h1>
          <button data-testid="pregen-button" onClick={startPregen}
            className="flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-sm px-5 py-2.5 rounded-xl transition-colors">
            <RefreshCw size={14} /> {tr("Pre-generate top 50")}
          </button>
        </div>
        {data.pregen?.status && (
          <p data-testid="pregen-status" className="mt-3 text-sm font-bold text-[#475569]">
            {tr("Batch status")}: <span className={data.pregen.status === "running" ? "text-[#EA580C]" : "text-[#16A34A]"}>{data.pregen.status}</span>
            {data.pregen.target ? ` — ${data.pregen.done}/${data.pregen.target}` : ""}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} data-testid={`coverage-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="border rounded-3xl p-5" style={{ background: s.bg, borderColor: `${s.color}40` }}>
            <s.icon size={18} style={{ color: s.color }} />
            <div className="text-2xl font-extrabold text-[#0F2557] mt-2">{s.value}</div>
            <div className="text-xs font-bold text-[#64748B] mt-1">{tr(s.label)}</div>
          </div>
        ))}
      </div>

      <h2 className="text-xl font-extrabold text-[#0F2557] mt-12">{tr("By continent")}</h2>
      <div className="mt-4 border border-[#E5EDF7] rounded-3xl overflow-hidden">
        <table className="w-full text-sm" data-testid="coverage-region-table">
          <thead>
            <tr className="bg-[#F6FAFF] text-left text-xs font-extrabold text-[#475569] uppercase tracking-wider">
              <th className="px-5 py-3">{tr("Region")}</th>
              <th className="px-5 py-3">{tr("Countries")}</th>
              <th className="px-5 py-3">{tr("Portals ready")}</th>
              <th className="px-5 py-3">{tr("Photos")}</th>
              <th className="px-5 py-3">{tr("Population data")}</th>
              <th className="px-5 py-3 w-40">{tr("Completion")}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.regions).sort().map(([region, r]) => (
              <tr key={region} className="border-t border-[#EFF4FB]">
                <td className="px-5 py-3 font-extrabold text-[#0F2557]">{tr(region)}</td>
                <td className="px-5 py-3 text-[#334155]">{r.total}</td>
                <td className="px-5 py-3 text-[#334155]">{r.enriched}</td>
                <td className="px-5 py-3 text-[#334155]">{r.with_photo}</td>
                <td className="px-5 py-3 text-[#334155]">{r.with_population}</td>
                <td className="px-5 py-3">
                  <div className="h-2 rounded-full bg-[#EAF2FD] overflow-hidden">
                    <div className="h-full rounded-full bg-[#16A34A]" style={{ width: `${pct(r.enriched, r.total)}%` }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#94A3B8] mt-4">{tr("Sources")}: {data.sources?.facts} · {data.sources?.population}. {tr("States/provinces stay gated until the country layer passes completeness standards.")}</p>

      {quality && (
        <div data-testid="quality-board" className="mt-14">
          <h2 className="text-xl font-extrabold text-[#0F2557] flex items-center gap-2"><Gauge size={19} className="text-[#7C3AED]" /> {tr("Quality Score (§39)")}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <div data-testid="quality-average" className="border border-[#D8CCF2] bg-[#FAF5FF] rounded-3xl p-5">
              <div className="text-2xl font-extrabold text-[#0F2557]">{quality.average}/100</div>
              <div className="text-xs font-bold text-[#64748B] mt-1">{tr("Average score")}</div>
            </div>
            <div className="border border-[#BBF7D0] bg-[#F0FDF4] rounded-3xl p-5">
              <div className="text-2xl font-extrabold text-[#0F2557]">{quality.passing_90}</div>
              <div className="text-xs font-bold text-[#64748B] mt-1">{tr("Countries scoring ≥90")}</div>
            </div>
            <div data-testid="quality-gate" className={`border rounded-3xl p-5 ${quality.gate.passed ? "border-[#BBF7D0] bg-[#F0FDF4]" : "border-[#FED7AA] bg-[#FFF7ED]"}`}>
              <div className={`text-2xl font-extrabold ${quality.gate.passed ? "text-[#15803D]" : "text-[#EA580C]"}`}>{quality.gate.passed ? tr("OPEN") : tr("HOLDING")}</div>
              <div className="text-xs font-bold text-[#64748B] mt-1">{tr("Country-first gate")} — {quality.gate.passing_pct}% / {quality.gate.passing_threshold}% {tr("required")}</div>
            </div>
            <div className="border border-[#BFD4F2] bg-[#F6FAFF] rounded-3xl p-5">
              <div className="flex items-center gap-2 text-[#15803D] font-extrabold text-sm"><ShieldCheck size={15} /> {tr("Platform-wide")}</div>
              <div className="text-xs font-bold text-[#64748B] mt-2">{tr("Translation")} ✓ · TTS ✓ · SEO ✓</div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-8">
            <span className="text-xs font-extrabold text-[#475569] uppercase tracking-wider">{tr("Lowest scores")}:</span>
            {["All", ...Object.keys(data.regions).sort()].map((r) => (
              <button key={r} data-testid={`quality-filter-${r.toLowerCase()}`} onClick={() => setRegionFilter(r)}
                className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${regionFilter === r ? "bg-[#7C3AED] border-[#7C3AED] text-white" : "bg-white border-[#E2E8F0] text-[#475569] hover:border-[#7C3AED]"}`}>
                {tr(r)}
              </button>
            ))}
          </div>
          <div className="mt-4 border border-[#E5EDF7] rounded-3xl overflow-hidden">
            <table className="w-full text-sm" data-testid="quality-table">
              <thead>
                <tr className="bg-[#FAF5FF] text-left text-xs font-extrabold text-[#475569] uppercase tracking-wider">
                  <th className="px-5 py-3">{tr("Country")}</th>
                  <th className="px-5 py-3">{tr("Facts")} /25</th>
                  <th className="px-5 py-3">{tr("Photo")} /15</th>
                  <th className="px-5 py-3">{tr("Content")} /50</th>
                  <th className="px-5 py-3">{tr("Cities")} /10</th>
                  <th className="px-5 py-3 w-44">{tr("Score")}</th>
                </tr>
              </thead>
              <tbody>
                {quality.countries.filter((c) => regionFilter === "All" || c.region === regionFilter).slice(0, 20).map((c) => (
                  <tr key={c.slug} className="border-t border-[#EFF4FB]" data-testid="quality-row">
                    <td className="px-5 py-2.5"><a href={`/country/${c.slug}`} className="font-extrabold text-[#0F2557] hover:text-[#7C3AED]">{c.flag} {c.name}</a></td>
                    <td className="px-5 py-2.5 text-[#334155]">{c.breakdown.facts}</td>
                    <td className="px-5 py-2.5 text-[#334155]">{c.breakdown.photo}</td>
                    <td className="px-5 py-2.5 text-[#334155]">{c.breakdown.content}</td>
                    <td className="px-5 py-2.5 text-[#334155]">{c.breakdown.cities}</td>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-[#F1F6FD] overflow-hidden">
                          <div className={`h-full rounded-full ${c.score >= 90 ? "bg-[#16A34A]" : c.score >= 50 ? "bg-[#EA580C]" : "bg-[#DC2626]"}`} style={{ width: `${c.score}%` }} />
                        </div>
                        <span className="text-xs font-extrabold text-[#0F2557] w-8">{c.score}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {corrections && (
        <div data-testid="corrections-queue" className="mt-14">
          <h2 className="text-xl font-extrabold text-[#0F2557] flex items-center gap-2">
            <Flag size={19} className="text-[#B45309]" /> {tr("Contributor Corrections")}
            <span className="text-xs font-bold text-[#64748B] bg-[#FFFBEB] border border-[#FDE68A] rounded-full px-3 py-1">
              {corrections.counts.pending} {tr("pending")} · {corrections.counts.reviewed} {tr("reviewed")}
            </span>
          </h2>
          <div className="mt-4 space-y-3">
            {corrections.items.length === 0 && (
              <p className="text-sm text-[#64748B] border border-[#E5EDF7] rounded-2xl p-5">{tr("No pending corrections — the queue is clear.")}</p>
            )}
            {corrections.items.map((c) => (
              <div key={c.id} data-testid="correction-item" className="border border-[#FDE68A] bg-[#FFFBEB] rounded-2xl p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <a href={c.url || "#"} className="font-extrabold text-sm text-[#0F2557] hover:text-[#B45309]">{c.place_name}</a>
                  <span className="text-[0.6rem] font-extrabold uppercase tracking-widest text-[#B45309] bg-white border border-[#FDE68A] rounded-full px-2.5 py-1">{c.section}</span>
                  <span className="text-[0.6rem] font-semibold text-[#94A3B8] ml-auto">{c.author} · {new Date(c.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{c.message}</p>
                <button data-testid="correction-review-button" onClick={() => reviewCorrection(c.id)}
                  className="mt-3 text-xs font-extrabold text-white bg-[#B45309] hover:bg-[#92400E] rounded-full px-4 py-2 transition-colors">
                  {tr("Mark reviewed")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
